
import { useState } from "react";
import { Pencil, Trash, Upload } from "lucide-react";

export default function EditableField({ field, onUpdate, onDelete, preview }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(field);
  const saveChanges = () => {
    onUpdate(local);
    setEditing(false);
  };

  return (
    <div className="relative bg-white">
      {field.type === "Heading1" && (
        <div className="mt-2 mb-8 relative">
          <div className="flex justify-between items-start">
            {editing ? (
              <input
                type="text"
                value={local.label}
                onChange={(e) => setLocal({ ...local, label: e.target.value })}
                className="text-2xl font-bold w-full border-b focus:outline-none"
                placeholder="Section Title"
              />
            ) : (
              <h2 className="text-2xl font-semibold text-gray-900">
                {field.label}
              </h2>
            )}
            {!preview && (
              <div className="flex gap-2 ml-2">
                <button
                  onClick={() => setEditing(!editing)}
                  className="text-gray-500 hover:text-blue-600"
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={onDelete}
                  className="text-gray-500 hover:text-red-600"
                >
                  <Trash size={16} />
                </button>
              </div>
            )}
          </div>

          {editing && (
            <div className="flex justify-end mt-2">
              <button
                onClick={saveChanges}
                className="text-sm text-[#0088FF] hover:underline"
              >
                Save
              </button>
            </div>
          )}
        </div>
      )}

      {field.type === "Heading2" && (
        <div className="mt-2 mb-6 relative">
          <div className="flex justify-between items-start">
            {editing ? (
              <input
                type="text"
                value={local.label}
                onChange={(e) => setLocal({ ...local, label: e.target.value })}
                className="text-lg font-semibold w-full border-b focus:outline-none"
                placeholder="Subsection Title"
              />
            ) : (
              <h3 className="text-lg font-semibold text-gray-800">
                {field.label}
              </h3>
            )}
            {!preview && (
              <div className="flex gap-2 ml-2">
                <button
                  onClick={() => setEditing(!editing)}
                  className="text-gray-500 hover:text-blue-600"
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={onDelete}
                  className="text-gray-500 hover:text-red-600"
                >
                  <Trash size={16} />
                </button>
              </div>
            )}
          </div>
          {editing && (
            <div className="flex justify-end mt-2">
              <button
                onClick={saveChanges}
                className="text-sm text-[#0088FF] hover:underline"
              >
                Save
              </button>
            </div>
          )}
        </div>
      )}

      {/* Shared Label + Icons (for all except headings) */}
      {field.type !== "Heading1" && field.type !== "Heading2" && (
        <div className="flex justify-between items-start mb-2">
          {editing ? (
            <input
              type="text"
              value={local.label}
              onChange={(e) => setLocal({ ...local, label: e.target.value })}
              className="text-sm font-medium w-full border-b focus:outline-none"
              placeholder="Label"
            />
          ) : (
            <label className="text-sm font-medium text-gray-800">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
          {!preview && (
            <div className="flex gap-2 ml-2">
              <button
                onClick={() => setEditing(!editing)}
                className="text-gray-500 hover:text-blue-600"
              >
                <Pencil size={16} />
              </button>
              <button
                onClick={onDelete}
                className="text-gray-500 hover:text-red-600"
              >
                <Trash size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Body */}
      {field.type === "Body" && (
        <>
          <textarea
            value={editing ? local.content || "" : local.content || ""}
            onChange={(e) =>
              editing && setLocal({ ...local, content: e.target.value })
            }
            className="w-full border px-3 py-2 rounded text-sm resize-none h-24"
            placeholder={
              editing
                ? local.placeholder || "Enter body content..."
                : local.placeholder
            }
            disabled={!editing}
          />
          {editing && (
            <div className="mt-3">
              <input
                type="text"
                value={local.placeholder}
                onChange={(e) =>
                  setLocal({ ...local, placeholder: e.target.value })
                }
                className="w-full border px-2 py-1 rounded text-sm"
                placeholder="Custom placeholder"
              />
            </div>
          )}
        </>
      )}

      {/* Text Field */}
      {field.type === "TextField" && (
        <input
          type="text"
          placeholder={editing ? local.placeholder : field.placeholder}
          onChange={(e) =>
            editing && setLocal({ ...local, placeholder: e.target.value })
          }
          className="w-full border px-3 py-2 rounded text-sm"
          disabled={!editing}
        />
      )}

      {/* Dropdown */}
      {field.type === "Dropdown" && (
        <>
          <select
            className="w-full border px-3 py-2 rounded text-sm"
            disabled={!editing}
            onChange={(e) =>
              editing && setLocal({ ...local, selected: e.target.value })
            }
          >
            {local.options?.map((opt, i) => (
              <option key={i} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {editing && (
            <div className="mt-2 space-y-1">
              {local.options?.map((opt, i) => (
                <input
                  key={i}
                  type="text"
                  value={opt}
                  onChange={(e) => {
                    const updated = [...local.options];
                    updated[i] = e.target.value;
                    setLocal({ ...local, options: updated });
                  }}
                  className="w-full border px-2 py-1 rounded text-xs"
                  placeholder={`Option ${i + 1}`}
                />
              ))}
              <button
                onClick={() =>
                  setLocal({
                    ...local,
                    options: [...(local.options || []), ""],
                  })
                }
                className="text-xs text-blue-600 hover:underline"
              >
                + Add Option
              </button>
            </div>
          )}
        </>
      )}

      {/* Checkbox Group */}
      {field.type === "Checkbox" && (
        <div className="flex flex-col gap-2 mt-2">
          {local.options?.map((opt, i) => (
            <label
              key={i}
              className="flex items-center gap-2 text-sm text-gray-700"
            >
              <input type="checkbox" className="accent-blue-600" disabled />
              {editing ? (
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => {
                    const updated = [...local.options];
                    updated[i] = e.target.value;
                    setLocal({ ...local, options: updated });
                  }}
                  className="border px-2 py-1 rounded text-xs"
                  placeholder={`Option ${i + 1}`}
                />
              ) : (
                <span>{opt}</span>
              )}
            </label>
          ))}
          {editing && (
            <button
              onClick={() =>
                setLocal({ ...local, options: [...(local.options || []), ""] })
              }
              className="text-xs text-blue-600 hover:underline mt-1"
            >
              + Add Option
            </button>
          )}
        </div>
      )}

      {/* Media Upload */}
      {(field.type === "UploadFile" || field.type === "UploadLogo") && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:border-blue-400 transition mt-2 relative">
          <Upload className="w-8 h-8 text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">
            Click to upload or drag and drop
          </p>
          <input
            type="file"
            className="absolute inset-0 opacity-0 cursor-pointer"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                setLocal({ ...local, fileName: file.name });
                onUpdate({ ...local, fileName: file.name });
              }
            }}
          />
        </div>
      )}

      {/* Required Toggle + Save */}
      {editing && field.type !== "Heading1" && field.type !== "Heading2" && (
        <div className="flex justify-between items-center mt-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={local.required}
              onChange={(e) =>
                setLocal({ ...local, required: e.target.checked })
              }
            />
            <span>Required</span>
          </label>
          <button
            onClick={() => {
              onUpdate(local);
              setEditing(false);
            }}
            className="text-sm text-[#0088FF] hover:underline"
          >
            Save
          </button>
        </div>
      )}
    </div>
  );
}
